import { baseDataTypeKeys } from "@redotech/redo-flows-core-schema-common/type-system/data-types/base-types";
import { removeGenericFromType } from "@redotech/redo-flows-core-schema-common/type-system/data-types/generic-types";
import {
  Schema,
  SchemaField,
} from "@redotech/redo-flows-core-schema-common/type-system/schema";
import {
  NonSchemaReferenceVariable,
  NonSchemaReferenceVariableName,
} from "./marketing/non-schema-variables";
import { schemas, SchemaType } from "./schemas";

// Only surface PRODUCT_LINK in product-specific triggers
const PRODUCT_LINK_SCHEMA_TYPES = new Set<SchemaType>([
  SchemaType.MARKETING_LOW_INVENTORY,
  SchemaType.MARKETING_PRICE_DROP,
]);

// Points-to-earn needs a cart subtotal, carried by checkout abandonment
// instances or fetched live from the Shopify cart abandonment cart.
const SMILE_POINTS_TO_EARN_SCHEMA_TYPES = new Set<SchemaType>([
  SchemaType.MARKETING_CHECKOUT_ABANDONMENT,
  SchemaType.MARKETING_BIGCOMMERCE_CHECKOUT_ABANDONMENT,
  SchemaType.MARKETING_COMMENTSOLD_CHECKOUT_ABANDONMENT,
  SchemaType.MARKETING_CART_ABANDONMENT,
]);

/**
 * Filters schema fields based on enabled integrations and visibility rules.
 *
 * @param schema - The schema to extract fields from
 * @param enabledIntegrations - Record of integration names to enabled status
 * @returns Array of SchemaField objects that pass all filters
 */
export function getVisibleDynamicVariables(
  schema: Schema | undefined,
  enabledIntegrations: Record<string, boolean>,
  schemaType?: SchemaType,
): SchemaField[] {
  const allFields = [
    ...Object.entries(NonSchemaReferenceVariable),
    ...(schema ? Object.entries(schema.fields) : []),
  ];

  const filteredFields = allFields
    // Filter by data type (base types only)
    .filter(([, field]) => {
      const baseType = removeGenericFromType(field.dataType);
      return baseDataTypeKeys.includes(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        baseType as (typeof baseDataTypeKeys)[number],
      );
    })
    .filter(([, field]) => field.shouldInclude?.(enabledIntegrations) ?? true)
    .filter(([, field]) => !field.hideFromInsertionModal)
    .filter(
      ([key]) =>
        key !== NonSchemaReferenceVariableName.PRODUCT_LINK ||
        (schemaType !== undefined && PRODUCT_LINK_SCHEMA_TYPES.has(schemaType)),
    )
    .filter(
      ([key]) =>
        key !== NonSchemaReferenceVariableName.SMILE_POINTS_TO_EARN ||
        (schemaType !== undefined &&
          SMILE_POINTS_TO_EARN_SCHEMA_TYPES.has(schemaType)),
    )
    .map(([key, field]) => ({ ...field, key }) satisfies SchemaField);

  // Add NonSchemaReferenceVariable fields (always available)
  return filteredFields;
}

/**
 * Builder-facing entry point: resolves the schema for a schemaType and threads
 * the schemaType through, so PRODUCT_LINK scoping is always applied.
 */
export function getVisibleDynamicVariablesForSchemaType(
  schemaType: SchemaType,
  enabledIntegrations: Record<string, boolean>,
): SchemaField[] {
  return getVisibleDynamicVariables(
    schemas[schemaType],
    enabledIntegrations,
    schemaType,
  );
}
